import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { getPackages, createPackage, updatePackage, deletePackage, getClientPackages, issueClientPackage, usePackageSession, getServices } from '../../services/api';
import { useConfirm } from '../../components/ConfirmDialog';
import { formatDate } from '../../utils/formatDateTime';
import PhoneInput from '../../components/PhoneInput';
import { formatPhoneBR } from '../../utils/phone';
import MoneyInput, { formatMoneyBRL } from '../../components/MoneyInput';

const emptyPkg   = { name: '', description: '', service_id: '', sessions: 5, price: 0, valid_days: 365, active: true };
const emptyIssue = { package_id: '', client_name: '', client_phone: '', client_email: '' };

export default function TenantPackages() {
  const confirm = useConfirm();
  const [packages, setPackages] = useState([]);
  const [clientPkgs, setClientPkgs] = useState([]);
  const [services, setServices] = useState([]);
  const [tab,      setTab]      = useState('packages');
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState(null);
  const [pkgForm,  setPkgForm]  = useState(emptyPkg);
  const [issueForm, setIssueForm] = useState(emptyIssue);
  const [saving,   setSaving]   = useState(false);
  const [cpSearch, setCpSearch] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([getPackages(), getClientPackages({ status: 'active' }), getServices()])
      .then(([p, cp, s]) => { setPackages(p.data.packages || []); setClientPkgs(cp.data.client_packages || []); setServices(s.data.services || []); })
      .catch(() => toast.error('Erro ao carregar pacotes'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSavePkg = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...pkgForm, service_id: pkgForm.service_id || null };
      if (modal === 'create') { await createPackage(payload); toast.success('Pacote criado'); }
      else { await updatePackage(modal.id, payload); toast.success('Pacote atualizado'); }
      setModal(null); load();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const onDeletePkg = async id => {
    const ok = await confirm({
      title: 'Remover pacote?',
      message: 'O pacote não estará mais disponível para venda. Pacotes já emitidos para clientes continuam valendo normalmente até serem consumidos ou expirarem.',
      confirmText: 'Remover',
      variant: 'danger',
    });
    if (!ok) return;
    try { await deletePackage(id); toast.success('Pacote removido'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Erro'); }
  };

  const onIssue = async e => {
    e.preventDefault();
    setSaving(true);
    try { await issueClientPackage(issueForm); toast.success('Pacote emitido para o cliente!'); setModal(null); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Erro ao emitir'); }
    finally { setSaving(false); }
  };

  const onUseSession = async id => {
    const ok = await confirm({
      title: 'Registrar uso de 1 sessão?',
      message: 'Isso marca uma sessão como utilizada no pacote deste cliente. A ação não pode ser desfeita — só faça ao finalizar um atendimento real.',
      confirmText: 'Registrar uso',
      variant: 'warning',
    });
    if (!ok) return;
    try { const r = await usePackageSession(id); toast.success(`Sessão registrada. Restam ${r.data.sessions_remaining}.`); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Erro'); }
  };

  const tabStyle = active => ({
    padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600,
    background: active ? 'var(--primary)' : 'transparent',
    color: active ? '#fff' : 'var(--gray-600)',
    border: 'none',
  });

  const fmtDate = formatDate;
  const fmtPrice = v => formatMoneyBRL(v);

  const filtered = clientPkgs.filter(cp => !cpSearch || cp.client_name.toLowerCase().includes(cpSearch.toLowerCase()) || cp.client_phone?.includes(cpSearch));

  return (
    <div className="page-body">
      <h1 style={{ margin: '0 0 20px', color: 'var(--gray-900)', fontSize: 22, fontWeight: 700 }}>Pacotes e Vouchers</h1>

      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: 'var(--gray-100)', padding: 4, borderRadius: 10, width: 'fit-content' }}>
        <button style={tabStyle(tab === 'packages')} onClick={() => setTab('packages')}>Pacotes</button>
        <button style={tabStyle(tab === 'clients')} onClick={() => setTab('clients')}>Vouchers emitidos</button>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner" /></div> : (
        <>
          {tab === 'packages' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <button className="btn btn-primary" onClick={() => { setPkgForm(emptyPkg); setModal('create'); }}>+ Novo pacote</button>
              </div>
              {packages.length === 0 ? (
                <div className="empty-state"><div style={{ fontSize: 40, marginBottom: 12 }}>📦</div><h3>Nenhum pacote cadastrado</h3><p>Crie pacotes de sessões com preço especial para seus clientes.</p></div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
                  {packages.map(p => (
                    <div key={p.id} className="card" style={{ padding: 20 }}>
                      <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--gray-900)', marginBottom: 4 }}>{p.name}</div>
                      {p.description && <div style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 10 }}>{p.description}</div>}
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 14 }}>
                        <span style={{ color: 'var(--gray-500)' }}>Sessões</span>
                        <strong>{p.sessions}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 14 }}>
                        <span style={{ color: 'var(--gray-500)' }}>Preço</span>
                        <strong style={{ color: 'var(--primary)' }}>{fmtPrice(p.price)}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, fontSize: 14 }}>
                        <span style={{ color: 'var(--gray-500)' }}>Validade</span>
                        <span>{p.valid_days} dias</span>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-primary" style={{ flex: 1, fontSize: 12 }} onClick={() => { setIssueForm({ ...emptyIssue, package_id: p.id }); setModal('issue'); }}>Emitir voucher</button>
                        <button className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => { setPkgForm({ name: p.name, description: p.description || '', service_id: p.service_id || '', sessions: p.sessions, price: p.price, valid_days: p.valid_days, active: !!p.active }); setModal(p); }}>✏️</button>
                        <button className="btn btn-danger" style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => onDeletePkg(p.id)}>🗑️</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {tab === 'clients' && (
            <>
              <div style={{ marginBottom: 16 }}>
                <input className="input" placeholder="Filtrar por nome ou telefone…" value={cpSearch} onChange={e => setCpSearch(e.target.value)} style={{ maxWidth: 320 }} />
              </div>
              <div className="table-container">
                <table className="table">
                  <thead><tr>
                    <th>Cliente</th><th>Telefone</th><th>Pacote</th><th>Sessões restantes</th><th>Vencimento</th><th>Status</th><th></th>
                  </tr></thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--gray-400)' }}>Nenhum voucher encontrado.</td></tr>
                    ) : filtered.map(cp => (
                      <tr key={cp.id}>
                        <td data-label="Cliente"><strong>{cp.client_name}</strong></td>
                        <td data-label="Telefone">{formatPhoneBR(cp.client_phone)}</td>
                        <td data-label="Pacote">{cp.package_name}</td>
                        <td data-label="Sessões restantes">{cp.sessions_remaining} / {cp.total_sessions}</td>
                        <td data-label="Vencimento">{fmtDate(cp.expires_at)}</td>
                        <td data-label="Status"><span className={`badge badge-${cp.status === 'active' ? 'success' : cp.status === 'exhausted' ? 'error' : 'info'}`}>{cp.status}</span></td>
                        <td>
                          {cp.status === 'active' && (
                            <button className="btn btn-primary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => onUseSession(cp.id)}>Usar sessão</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {/* Package form modal */}
      {(modal === 'create' || (modal && modal.name && modal !== 'issue')) && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{modal === 'create' ? 'Novo pacote' : 'Editar pacote'}</h3>
              <button className="modal-close" onClick={() => setModal(null)}>✕</button>
            </div>
            <form onSubmit={onSavePkg}>
              <div className="modal-body">
                <div className="form-group"><label className="label">Nome *</label>
                  <input className="input" value={pkgForm.name} onChange={e => setPkgForm(f => ({ ...f, name: e.target.value }))} required />
                </div>
                <div className="form-group"><label className="label">Descrição</label>
                  <input className="input" value={pkgForm.description} onChange={e => setPkgForm(f => ({ ...f, description: e.target.value }))} />
                </div>
                <div className="form-group"><label className="label">Serviço (opcional)</label>
                  <select className="input" value={pkgForm.service_id} onChange={e => setPkgForm(f => ({ ...f, service_id: e.target.value }))}>
                    <option value="">Qualquer serviço</option>
                    {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div className="form-group"><label className="label">Quantidade de sessões *</label>
                  <input className="input" type="number" min="1" value={pkgForm.sessions} onChange={e => setPkgForm(f => ({ ...f, sessions: parseInt(e.target.value) || 1 }))} required />
                </div>
                <div className="form-group"><label className="label">Preço (R$) *</label>
                  <MoneyInput
                    className="input"
                    value={pkgForm.price}
                    onChange={v => setPkgForm(f => ({ ...f, price: v }))}
                    min={0}
                    required
                  />
                </div>
                <div className="form-group"><label className="label">Validade (dias)</label>
                  <input className="input" type="number" min="1" value={pkgForm.valid_days} onChange={e => setPkgForm(f => ({ ...f, valid_days: parseInt(e.target.value) || 365 }))} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
                <button type="submit" className={`btn btn-primary${saving ? ' btn-loading' : ''}`} disabled={saving}>{saving ? '' : 'Salvar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Issue voucher modal */}
      {modal === 'issue' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Emitir voucher para cliente</h3>
              <button className="modal-close" onClick={() => setModal(null)}>✕</button>
            </div>
            <form onSubmit={onIssue}>
              <div className="modal-body">
                <div className="form-group"><label className="label">Pacote</label>
                  <select className="input" value={issueForm.package_id} onChange={e => setIssueForm(f => ({ ...f, package_id: e.target.value }))} required>
                    <option value="">Selecione…</option>
                    {packages.map(p => <option key={p.id} value={p.id}>{p.name} ({p.sessions} sessões)</option>)}
                  </select>
                </div>
                <div className="form-group"><label className="label">Nome do cliente *</label>
                  <input className="input" value={issueForm.client_name} onChange={e => setIssueForm(f => ({ ...f, client_name: e.target.value }))} required />
                </div>
                <div className="form-group"><label className="label">Telefone *</label>
                  <PhoneInput
                    value={issueForm.client_phone}
                    onChange={(masked, normalized) => setIssueForm(f => ({ ...f, client_phone: normalized || masked }))}
                    required
                  />
                </div>
                <div className="form-group"><label className="label">E-mail</label>
                  <input className="input" type="email" value={issueForm.client_email} onChange={e => setIssueForm(f => ({ ...f, client_email: e.target.value }))} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
                <button type="submit" className={`btn btn-primary${saving ? ' btn-loading' : ''}`} disabled={saving}>{saving ? '' : 'Emitir voucher'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
